import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-tenant-switcher.css?inline';
import { parseJsonArray } from '../../lib/portal-data.js';
import '../pk-button/pk-button.js';

/** One membership (contract `Membership`, `/me` `memberships[]`). */
export interface PkMembershipRecord {
    tenantId: string;
    tenantName?: string | null;
    tier?: string | null;
    role?: string | null;
}

/**
 * `<pk-tenant-switcher>` — switch the current tenant. Renders **nothing** unless the user belongs to more
 * than one tenant (`memberships.length > 1`): ActivityLog users have exactly one, Momentus users may have
 * several.
 *
 * With `action` set it is a form (`<select name>` + Switch) that POSTs the chosen tenant id to the CMS,
 * which re-issues the session for that tenant. Without `action` it only emits `pk-tenant-switch` on
 * change — the product wires the switch itself (contract v1 has no switch endpoint).
 *
 * Attributes:
 *   - `memberships` — JSON array of memberships (or the `memberships` property).
 *   - `current`     — the current tenant id (preselected).
 *   - `action`      — POST URL; when absent the component is event-only.
 *   - `name`        — field name (default `tenantId`).
 *   - `csrf-token` / `csrf-name` — anti-forgery hidden input.
 *
 * Events:
 *   - `pk-tenant-switch` — `CustomEvent<{ tenantId: string }>`, cancelable; fired on change (event-only
 *     mode) or as the form submits (`preventDefault()` stops the POST).
 */
export class PkTenantSwitcher extends HTMLElement {
    static readonly tagName = 'pk-tenant-switcher';

    static get observedAttributes(): string[] {
        return ['memberships', 'current', 'action', 'name', 'csrf-token', 'csrf-name'];
    }

    private readonly _root: ShadowRoot;
    private _memberships: PkMembershipRecord[] | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'memberships') this._memberships = null;
        if (this.isConnected) this._render();
    }

    /** The memberships (property wins over the attribute once set). */
    get memberships(): PkMembershipRecord[] {
        return this._memberships ?? parseJsonArray<PkMembershipRecord>(this.getAttribute('memberships'));
    }

    set memberships(value: PkMembershipRecord[]) {
        this._memberships = Array.isArray(value) ? value : [];
        if (this.isConnected) this._render();
    }

    private _selected(): string {
        return (this._root.querySelector('select') as HTMLSelectElement | null)?.value ?? '';
    }

    private _emit(tenantId: string): boolean {
        return this.dispatchEvent(new CustomEvent('pk-tenant-switch', {
            bubbles: true, composed: true, cancelable: true, detail: { tenantId },
        }));
    }

    private readonly _onChange = (): void => {
        if (!this.getAttribute('action')) this._emit(this._selected());
    };

    private readonly _onSubmit = (e: Event): void => {
        if (!this._emit(this._selected())) e.preventDefault();
    };

    private _select(list: PkMembershipRecord[], current: string, name: string): TemplateResult {
        return html`
            <select name=${name} aria-label="Organisation" @change=${this._onChange}>
                ${list.map((m) => html`
                    <option value=${m.tenantId} ?selected=${m.tenantId === current}>${m.tenantName || m.tenantId}</option>
                `)}
            </select>
        `;
    }

    private template(): TemplateResult | typeof nothing {
        const list = this.memberships;
        if (list.length <= 1) return nothing;

        const current = this.getAttribute('current') ?? '';
        const action = this.getAttribute('action') ?? '';
        const name = this.getAttribute('name') ?? 'tenantId';
        const token = this.getAttribute('csrf-token') ?? '';
        const csrfName = this.getAttribute('csrf-name') ?? '__RequestVerificationToken';

        return html`
            <style>${styles}</style>
            ${action
                ? html`
                    <form method="post" action=${action}>
                        ${token ? html`<input type="hidden" name=${csrfName} .value=${token} />` : ''}
                        ${this._select(list, current, name)}
                        <pk-button variant="secondary" type="submit">Switch</pk-button>
                    </form>
                `
                : html`<div class="switcher">${this._select(list, current, name)}</div>`}
        `;
    }

    private _render(): void {
        const list = this.memberships;
        this.toggleAttribute('hidden', list.length <= 1);
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTenantSwitcher.tagName)) {
    customElements.define(PkTenantSwitcher.tagName, PkTenantSwitcher);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-tenant-switcher': PkTenantSwitcher;
    }
}
